import { useState, useRef, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  ChatBubbleLeftRightIcon,
  MicrophoneIcon,
  PaperAirplaneIcon,
  PhoneIcon,
  VideoCameraIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Badge } from '../components/ui/Badge'
import { groqChatStream } from '../lib/groq'
import { clsx } from 'clsx'

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } },
}

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
}

const channels = [
  { id: 'web', name: 'Website Widget', status: 'active', conversations: 234 },
  { id: 'whatsapp', name: 'WhatsApp Business', status: 'active', conversations: 156 },
  { id: 'telegram', name: 'Telegram Bot', status: 'active', conversations: 89 },
  { id: 'voice', name: 'Voice Assistant', status: 'active', conversations: 45 },
  { id: 'sms', name: 'SMS Gateway', status: 'pending', conversations: 0 },
]

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
}

const suggestedResponses = [
  'What services do you offer?',
  'Tell me about pricing plans',
  'How can I get started?',
  'Do you offer enterprise support?',
]

export const AIAssistant = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: "Hello! I'm IntelCore's AI Assistant. I can help you with our Enterprise Intelligence platform, services, pricing, and more. How can I help you today?",
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    },
  ])
  const [message, setMessage] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [selectedChannel, setSelectedChannel] = useState(channels[0])
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const streamAbortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSend = async (text?: string) => {
    const content = text || message.trim()
    if (!content || isStreaming) return

    const userMsg: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    }

    setMessages((prev) => [...prev, userMsg])
    setMessage('')
    setIsStreaming(true)

    const aiMsgId = `ai-${Date.now()}`
    const aiMsg: Message = {
      id: aiMsgId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    }
    setMessages((prev) => [...prev, aiMsg])

    try {
      const history = [...messages, userMsg].map((m) => ({
        role: m.role as 'user' | 'assistant',
        content: m.content,
      }))

      await groqChatStream(
        content,
        history.slice(0, -1),
        (chunk) => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === aiMsgId ? { ...m, content: m.content + chunk } : m,
            ),
          )
        },
        () => {
          setIsStreaming(false)
        },
      )
    } catch (error) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === aiMsgId
            ? { ...m, content: "I apologize — I'm having trouble connecting right now. Please try again in a moment." }
            : m,
        ),
      )
      setIsStreaming(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="show"
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={item} className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-metallic-900 flex items-center gap-2">
            <SparklesIcon className="w-6 h-6 text-primary-500" />
            AI Assistant
          </h1>
          <p className="text-metallic-500 mt-1">
            Powered by Groq — Real-time AI chat and voice agent management.
          </p>
        </div>
        <Badge variant="success" dot>
          Groq Connected
        </Badge>
      </motion.div>

      {/* Channel Stats */}
      <motion.div variants={item} className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {channels.map((channel) => (
          <Card
            key={channel.id}
            hover
            className={clsx(
              'text-center cursor-pointer transition-all',
              selectedChannel?.id === channel.id && 'ring-2 ring-primary-500',
            )}
            onClick={() => setSelectedChannel(channel)}
          >
            <Badge
              variant={channel.status === 'active' ? 'success' : 'warning'}
              dot
              className="mb-2"
            >
              {channel.status}
            </Badge>
            <p className="text-2xl font-bold text-metallic-900">{channel.conversations}</p>
            <p className="text-sm text-metallic-500 mt-1">{channel.name}</p>
          </Card>
        ))}
      </motion.div>

      {/* Main Content */}
      <motion.div variants={item} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chat Interface */}
        <Card className="lg:col-span-3 flex flex-col">
          {/* Chat Header */}
          <div className="flex items-center justify-between pb-4 border-b border-metallic-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center">
                <SparklesIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-medium text-metallic-900">IntelCore AI</p>
                <p className="text-sm text-metallic-500">
                  {isStreaming ? 'Thinking...' : 'Online — Powered by Groq Llama 3.3'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm">
                <PhoneIcon className="w-5 h-5" />
              </Button>
              <Button variant="ghost" size="sm">
                <VideoCameraIcon className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 p-4 space-y-4 min-h-[400px] max-h-[500px] overflow-y-auto">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={clsx(
                  'flex gap-3',
                  msg.role === 'user' && 'justify-end',
                )}
              >
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center flex-shrink-0">
                    <SparklesIcon className="w-4 h-4 text-white" />
                  </div>
                )}
                <div
                  className={clsx(
                    'rounded-2xl p-4 max-w-md',
                    msg.role === 'assistant'
                      ? 'bg-metallic-100 rounded-tl-none'
                      : 'bg-primary-500 text-white rounded-tr-none',
                  )}
                >
                  <p className={clsx(msg.role === 'assistant' && 'text-metallic-700')}>
                    {msg.content || (
                      <span className="inline-flex gap-1">
                        <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                        <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                        <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                      </span>
                    )}
                  </p>
                  {msg.content && (
                    <p className={clsx(
                      'text-xs mt-2',
                      msg.role === 'assistant' ? 'text-metallic-400' : 'text-primary-200',
                    )}>
                      {msg.timestamp}
                    </p>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Suggested Responses */}
          <div className="px-4 py-2 border-t border-metallic-100">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {suggestedResponses.map((response, index) => (
                <button
                  key={index}
                  onClick={() => handleSend(response)}
                  disabled={isStreaming}
                  className="flex-shrink-0 px-3 py-1.5 bg-metallic-100 hover:bg-primary-100 text-sm text-metallic-600 hover:text-primary-600 rounded-full transition-colors disabled:opacity-50"
                >
                  {response}
                </button>
              ))}
            </div>
          </div>

          {/* Message Input */}
          <div className="p-4 border-t border-metallic-100">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsRecording(!isRecording)}
                className={clsx(isRecording && 'text-error-500')}
              >
                <MicrophoneIcon className="w-5 h-5" />
              </Button>
              <Input
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask me anything about IntelCore..."
                className="flex-1"
                disabled={isStreaming}
              />
              <Button
                size="sm"
                onClick={() => handleSend()}
                disabled={!message.trim() || isStreaming}
              >
                <PaperAirplaneIcon className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </Card>
      </motion.div>
    </motion.div>
  )
}
